import { useEffect, useState } from "react";
import { SignedScanUploadButton } from "@/components/messaging/SignedScanUploadButton";
import PrintPartsMenu from "@/components/messaging/PrintPartsMenu";
import api, { API, getToken } from "@/lib/api";
import { toast } from "sonner";
import { fetchPdfBlob } from "@/lib/printPdf";
import { Button } from "@/components/ui/button";
import { DocMetaButton } from "@/components/documents/DocumentMeta";
import { X, Download, FileText, Paperclip, CheckCircle, Envelope, Printer } from "@phosphor-icons/react";

/**
 * Slide-Over Drawer für Dokument-Vorschau (PDF/Image inline).
 *
 * Props:
 *  - docId        : string – ID des Dokuments
 *  - onClose      : () => void
 *  - mode         : "admin" (default für Verwalter-Portal) | "owner"
 *                   - admin → /documents/{id}/download (inline)
 *                   - owner → /owner-portal/documents/{id}/preview (mit ACL)
 *  - metaOverride : Optional – falls Caller bereits Metadaten hat (title, filename)
 */
export default function DocumentPreviewDrawer({ docId, onClose, mode = "admin", metaOverride = null }) {
  const [doc, setDoc] = useState(metaOverride);
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setContentType] = useState("");
  const [error, setError] = useState(null);
  // 2026-02 — Verknüpfte Dokumente (Anhänge + Zustellbestätigung + Umschlag)
  const [related, setRelated] = useState(null);
  const [activeDocId, setActiveDocId] = useState(docId);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && !document.querySelector('[role="dialog"][data-state="open"]')) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);


  useEffect(() => { setActiveDocId(docId); }, [docId]);

  useEffect(() => {
    if (!activeDocId || mode === "owner") { setRelated(null); return; }
    api.get(`/documents/${activeDocId}/related`)
      .then(({ data }) => setRelated(data))
      .catch(() => setRelated(null));
  }, [activeDocId, mode]);

  useEffect(() => {
    if (!activeDocId) return;
    let revoked = false;
    let createdUrl = null;
    setError(null);
    setBlobUrl(null);
    (async () => {
      try {
        // Meta laden (nur wenn nicht schon übergeben oder aktive ID gewechselt hat)
        try {
          if (mode === "owner") {
            const r1 = await api.get(`/owner-portal/documents`);
            setDoc(r1.data.find((d) => d.id === activeDocId) || null);
          } else {
            const r1 = await api.get(`/documents`, { params: {} });
            setDoc((r1.data || []).find((d) => d.id === activeDocId) || metaOverride || null);
          }
        } catch {
          /* metadata lookup is best-effort */
        }
        // Inhalt laden
        const url = mode === "owner"
          ? `${API}/owner-portal/documents/${activeDocId}/preview`
          : `${API}/documents/${activeDocId}/download`;
        const r = await fetch(url, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!r.ok) throw new Error("Preview nicht verfügbar");
        const ct = r.headers.get("content-type") || "";
        setContentType(ct);
        const blob = await r.blob();
        createdUrl = URL.createObjectURL(blob);
        if (!revoked) setBlobUrl(createdUrl);
      } catch (e) {
        if (!revoked) setError(e.message || "Vorschau fehlgeschlagen");
      }
    })();
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [activeDocId, mode, reloadKey]);  // eslint-disable-line

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = doc?.original_filename || "dokument";
    a.click();
  };

  const isPdf = contentType.includes("pdf");
  const isImg = contentType.startsWith("image/");

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="doc-preview-drawer">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      {/* Drawer */}
      <div className="ml-auto bg-white w-full max-w-3xl h-full shadow-2xl relative flex flex-col animate-in slide-in-from-right">
        <div className="border-b border-slate-200 px-6 py-4 flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0 flex-1 basis-60">
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Dokument</div>
            <div className="font-semibold text-slate-900 truncate text-sm">{doc?.title || "—"}</div>
            <div className="text-[11px] text-slate-500">{doc?.original_filename}</div>
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {mode === "admin" && doc?.id && <DocMetaButton doc={doc} onSaved={(d) => setDoc((x) => ({ ...x, ...d }))} />}
            {mode === "admin" && doc?.signature_status === "pending_scan" && (
              <SignedScanUploadButton documentId={doc.id} onDone={() => setReloadKey((k) => k + 1)}
                                      testId="doc-signed-scan-upload" />
            )}
            {doc?.signature_status === "signed_original" && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-800 border border-indigo-200"
                    data-testid="doc-signed-original-badge">Unterschriebenes Original</span>
            )}
            {mode === "admin" && doc?.has_unsigned_version && (
              <Button variant="outline" size="sm" data-testid="doc-print-unsigned" title="Ursprüngliches Schreiben ohne Unterschrift erneut ausdrucken"
                      onClick={async () => { try { const { blob } = await fetchPdfBlob(`/documents/${doc.id}/download?version=unsigned`); window.open(URL.createObjectURL(blob), "_blank"); } catch (e) { toast.error(e.message); } }}>
                <Printer size={14} className="mr-1" /> Ursprüngliches Schreiben
              </Button>
            )}
            {mode === "admin" && (related?.attachments || []).length > 0 && (
              <PrintPartsMenu documentId={activeDocId} attachmentCount={related.attachments.length} testId="doc-print-parts" />
            )}
            <Button variant="outline" size="sm" onClick={download} disabled={!blobUrl} data-testid="doc-preview-download">
              <Download size={14} className="mr-1" /> Herunterladen
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="doc-preview-close">
              <X size={18} />
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-slate-100 p-2">
          {!blobUrl && (
            <div className="h-full flex items-center justify-center text-sm text-slate-500">
              <FileText size={48} className="text-slate-300 mr-2" /> Vorschau wird geladen…
            </div>
          )}
          {blobUrl && isPdf && (
            <iframe title="PDF Vorschau" src={blobUrl} className="w-full h-full bg-white rounded" data-testid="doc-preview-pdf" />
          )}
          {blobUrl && isImg && (
            <div className="h-full flex items-center justify-center"><img src={blobUrl} alt={doc?.title} className="max-w-full max-h-full" data-testid="doc-preview-img" /></div>
          )}
          {blobUrl && !isPdf && !isImg && (
            <div className="h-full flex flex-col items-center justify-center text-slate-600">
              <FileText size={64} className="text-slate-300 mb-3" />
              <div className="text-sm">Vorschau nicht verfügbar.</div>
              <Button size="sm" className="mt-3" onClick={download}>Datei herunterladen</Button>
            </div>
          )}
        </div>

        {/* 2026-02 — Verknüpfte Dokumente (Anhänge / Zustellbestätigung / Umschlag) */}
        {related && mode === "admin" && (
          <RelatedDocsPanel
            related={related}
            activeDocId={activeDocId}
            onSelect={setActiveDocId}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Panel mit verknüpften Dokumenten am unteren Rand des Preview-Drawers.
 * Klicken auf einen Chip lädt das jeweilige Dokument im gleichen Drawer.
 */
const CHIP_STYLES = {
  proof:    { active: "bg-emerald-600 text-white border-emerald-600", idle: "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100" },
  envelope: { active: "bg-amber-600 text-white border-amber-600",     idle: "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100" },
  parent:   { active: "bg-slate-700 text-white border-slate-700",     idle: "bg-slate-50 text-slate-800 border-slate-200 hover:bg-slate-100" },
  attach:   { active: "bg-blue-600 text-white border-blue-600",       idle: "bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100" },
};

function RelatedChip({ d, kind, isActive, onSelect }) {
  const s = CHIP_STYLES[kind] || CHIP_STYLES.parent;
  return (
    <button
      type="button"
      onClick={() => onSelect(d.id)}
      className={`flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md border transition-colors ${isActive ? s.active : s.idle}`}
      data-testid={`related-chip-${d.id}`}
      title={d.title || d.original_filename}
    >
      {kind === "proof" && <CheckCircle size={12} />}
      {kind === "envelope" && <Envelope size={12} />}
      {kind === "attach" && <Paperclip size={12} />}
      {kind === "parent" && <FileText size={12} />}
      <span className="truncate max-w-[200px]">{d.title || d.original_filename}</span>
    </button>
  );
}

function RelatedDocsPanel({ related, activeDocId, onSelect }) {
  const parent = related.parent;
  const bundle = related.bundle;
  const attachments = related.attachments || [];
  const proofs = related.delivery_proofs || [];
  const envelopes = related.envelopes || [];
  const mainDoc = related.document;
  // 2026-07 — Umschlag on-demand generieren (nur wenn es ein Letter/Brief ist
  // und noch KEIN Umschlag verlinkt wurde, sonst würden Duplikate entstehen).
  const canCreateEnvelope = mainDoc
    && (mainDoc.doc_type === "letter" || mainDoc.category === "Schriftverkehr")
    && envelopes.length === 0;
  const canRegenerateProof = mainDoc
    && mainDoc.delivery_status_at
    && mainDoc.doc_type !== "delivery_proof"
    && mainDoc.doc_type !== "envelope";
  const [creatingEnvelope, setCreatingEnvelope] = useState(false);
  const [regeneratingProof, setRegeneratingProof] = useState(false);
  const createEnvelope = async () => {
    if (!mainDoc?.id) return;
    setCreatingEnvelope(true);
    try {
      const { data } = await api.post(`/messages/documents/${mainDoc.id}/envelope`);
      toast.success("Fensterumschlag erstellt");
      if (data?.envelope_document_id) onSelect(data.envelope_document_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Umschlag konnte nicht erstellt werden");
    } finally {
      setCreatingEnvelope(false);
    }
  };
  const regenerateProof = async () => {
    if (!mainDoc?.id) return;
    setRegeneratingProof(true);
    try {
      const { data } = await api.post(`/documents/${mainDoc.id}/regenerate-delivery-proof`);
      toast.success("Zustellbeleg neu erzeugt (SHA-256 & Timeline)");
      if (data?.proof_document_id) onSelect(data.proof_document_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Beleg konnte nicht erzeugt werden");
    } finally {
      setRegeneratingProof(false);
    }
  };
  const hasAny = parent || bundle || attachments.length || proofs.length || envelopes.length;
  const deliveryBadge = mainDoc?.delivery_status ? (
    <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full font-semibold ${
      mainDoc.delivery_status === "delivered" ? "bg-emerald-100 text-emerald-800"
        : mainDoc.delivery_status === "opened" ? "bg-blue-100 text-blue-800"
        : mainDoc.delivery_status === "bounced" || mainDoc.delivery_status === "complained" ? "bg-red-100 text-red-800"
        : "bg-slate-100 text-slate-700"
    }`} data-testid="doc-delivery-status">
      {mainDoc.delivery_status === "sent" && "✉ gesendet"}
      {mainDoc.delivery_status === "delivered" && "✓ zugestellt"}
      {mainDoc.delivery_status === "opened" && "👁 geöffnet"}
      {mainDoc.delivery_status === "bounced" && "✗ zurück (bounce)"}
      {mainDoc.delivery_status === "complained" && "✗ Spam"}
      {mainDoc.delivery_status === "delayed" && "⏳ verzögert"}
    </span>
  ) : null;

  if (!hasAny && !deliveryBadge && !canCreateEnvelope) return null;

  return (
    <div className="border-t border-slate-200 bg-white px-4 py-3 space-y-2" data-testid="related-docs-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Verknüpfte Dokumente</div>
        {deliveryBadge}
        {canCreateEnvelope && (
          <button
            type="button"
            onClick={createEnvelope}
            disabled={creatingEnvelope}
            className="ml-auto inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors disabled:opacity-50"
            data-testid="create-envelope-btn"
          >
            <Envelope size={12} /> {creatingEnvelope ? "Erzeuge…" : "Umschlag drucken"}
          </button>
        )}
        {canRegenerateProof && (
          <button
            type="button"
            onClick={regenerateProof}
            disabled={regeneratingProof}
            className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition-colors disabled:opacity-50 ${
              canCreateEnvelope ? "" : "ml-auto"
            }`}
            title="Erzeugt einen revisionssicheren Zustellbeleg mit SHA-256-Hash und vollständiger Event-Timeline."
            data-testid="regenerate-proof-btn"
          >
            {regeneratingProof ? "Erzeuge…" : "Zustellbeleg neu (mit Hash)"}
          </button>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {parent && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-slate-500">↑ Elterndokument:</span>
            <RelatedChip d={parent} kind="parent" isActive={parent.id === activeDocId} onSelect={onSelect} />
          </div>
        )}
        {bundle && <RelatedChip d={{ ...bundle, title: "Gesamt-PDF (Schreiben + Anlagen)" }} kind="parent" isActive={bundle.id === activeDocId} onSelect={onSelect} />}
        {attachments.map((d) => <RelatedChip key={d.id} d={d} kind="attach" isActive={d.id === activeDocId} onSelect={onSelect} />)}
        {proofs.map((d) => <RelatedChip key={d.id} d={d} kind="proof" isActive={d.id === activeDocId} onSelect={onSelect} />)}
        {envelopes.map((d) => <RelatedChip key={d.id} d={d} kind="envelope" isActive={d.id === activeDocId} onSelect={onSelect} />)}
      </div>
    </div>
  );
}
